import { useQueries } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useSearchTabs } from '@/hooks/useSearchTabs';
import { useSearchFilters } from '@/hooks/useSearchFilters';
import { useTenants } from '@/services/tenants/useTenants';
import {
  normalizeSelectedFilters,
  normalizeSearchQuery,
  getMetadataFiltersQueryParameter,
  getFoldersQueryParameter,
  getExtensionsQueryParameter,
} from '@/components/Search/utils';
import { SearchTabCounts } from '@/components/Search/types';
import { DATA_GRID_LAZY_PAGE_SIZE } from '@/components/DataGrid/definitions';
import { useFileUpload } from '@/services/fileUpload';
import { SelectedFilters } from '@/components/FilterData/types';

const EMPTY_COUNTS: SearchTabCounts = {};

const getSearchApiCall = (
  searchType: string,
  baseParameters: {
    assetId?: string;
    assetTenantId?: string;
    searchTenantId: string;
    searchQuery?: string;
    filters: ReturnType<typeof normalizeSelectedFilters>;
    page: number;
    perPage: number;
    files?: File[];
  },
  minThreshold?: number
) => {
  if (searchType === 'text') {
    return api.crossTenant.textSearch(baseParameters.searchTenantId, {
      searchQuery: baseParameters.searchQuery ?? '',
      filters: baseParameters.filters,
      page: baseParameters.page,
      perPage: baseParameters.perPage,
    });
  }

  if (searchType === 'image') {
    if (!baseParameters.files || baseParameters.files.length === 0) {
      return Promise.resolve({ data: { pageData: { total: 0 } } });
    }
    const filters: SelectedFilters = {
      extensions: baseParameters.filters.extensions ?? [],
      labels: baseParameters.filters.labels ?? [],
      folders: baseParameters.filters.folders ?? [],
      metadata: baseParameters.filters.metadata ?? {},
    };
    return api.crossTenant.visualSearch(baseParameters.searchTenantId, {
      files: baseParameters.files,
      searchQuery: baseParameters.searchQuery ?? '',
      metadataFilters: getMetadataFiltersQueryParameter(filters),
      folders: getFoldersQueryParameter(filters),
      extensions: getExtensionsQueryParameter(filters),
      page: `${baseParameters.page}`,
      perPage: `${baseParameters.perPage}`,
    });
  }

  if (!baseParameters.assetId || !baseParameters.assetTenantId) {
    return Promise.resolve({ data: { pageData: { total: 0 } } });
  }

  const { assetId, assetTenantId, ...rest } = baseParameters;
  const parameters = { ...rest, assetId, assetTenantId };

  switch (searchType) {
    case 'geometric': {
      return api.crossTenant.crossTenantGeometricSearch({ ...parameters, minThreshold });
    }
    case 'part': {
      return api.crossTenant.crossTenantPartSearch({ ...parameters, minThreshold: minThreshold ?? 80 });
    }
    case 'visual': {
      return api.crossTenant.crossTenantVisualSearch({ ...parameters, sizeThreshold: minThreshold });
    }
    case 'metadata': {
      return api.crossTenant.crossTenantMetadataSearch(parameters);
    }
    default: {
      return Promise.resolve({ data: { pageData: { total: 0 } } });
    }
  }
};

export const useSearchTabCounts = () => {
  const { searchTabs, shouldShowSearchTabs } = useSearchTabs();
  const { assetId, searchType, selectedFilters, minThreshold, searchQuery, isEmptySearch } = useSearchFilters();
  const { getTenantId } = useTenants();
  const currentTenantId = getTenantId();
  const { fileUpload } = useFileUpload();

  const normalizedFilters = normalizeSelectedFilters(selectedFilters);
  const normalizedSearchQuery = normalizeSearchQuery(searchQuery);

  const enabled =
    shouldShowSearchTabs &&
    !isEmptySearch &&
    !!searchType &&
    searchType !== 'image' &&
    !!currentTenantId &&
    (searchType === 'text' ? !!normalizedSearchQuery : !!assetId);

  const queries = useQueries({
    queries: searchTabs.map(tenant => {
      const tenantId = tenant?.tenantId;
      const baseParameters = {
        ...(searchType !== 'text' && searchType !== 'image' && { assetId, assetTenantId: currentTenantId }),
        ...(searchType === 'image' && { files: fileUpload?.files }),
        searchTenantId: tenantId,
        searchQuery: normalizedSearchQuery,
        filters: normalizedFilters,
        page: 1,
        perPage: DATA_GRID_LAZY_PAGE_SIZE,
      };

      return {
        queryKey: [
          'searchTabCounts',
          tenantId,
          searchType === 'text' || searchType === 'image' ? undefined : assetId,
          searchType,
          normalizedSearchQuery,
          JSON.stringify(normalizedFilters),
          searchType === 'text' || searchType === 'image' ? undefined : minThreshold,
          currentTenantId,
          ...(searchType === 'image' ? [fileUpload?.files?.map((f: File) => `${f.name}:${f.size}`).join(',')] : []),
        ],
        queryFn: () => getSearchApiCall(searchType, baseParameters, minThreshold),
        enabled: enabled && !!tenantId,
        select: (data: { data: { pageData: { total: number } } }) => data.data.pageData.total,
        retry: false,
      };
    }),
  });

  if (!enabled) {
    return { counts: EMPTY_COUNTS, loadingStates: {} };
  }

  const counts: SearchTabCounts = Object.fromEntries(
    searchTabs
      .map((tenant, index) => [tenant?.tenantId, queries[index].data])
      .filter(([tenantId, data]) => tenantId && data !== undefined)
  ) as SearchTabCounts;

  const loadingStates: Record<string, boolean> = Object.fromEntries(
    searchTabs.map((tenant, index) => [tenant?.tenantId, queries[index].isLoading]).filter(([tenantId]) => tenantId)
  );

  return { counts, loadingStates };
};
